// RoleSelector — full-screen modal overlay | user picks their role (Student / Counselor / School) | navigates to correct waitlist on selection | AnimatePresence fade+scale in

import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { colors } from './landingTokens'

const EASE = [0.22, 1, 0.36, 1] as const

interface RoleSelectorProps {
  isOpen: boolean
  onClose: () => void
}

interface RoleCard {
  label: string
  body: string
  cta: string
  subCta?: string
  color: string
  border: string
  bg: string
  borderHover: string
  bgHover: string
  route: string
}

const ROLES: RoleCard[] = [
  {
    label: 'Students',
    body: 'Build your story. Get into your dream school.',
    cta: 'Get Early Access →',
    color: colors.student,
    border: 'rgba(168,85,247,0.3)',
    bg: 'rgba(168,85,247,0.06)',
    borderHover: 'rgba(168,85,247,0.6)',
    bgHover: 'rgba(168,85,247,0.12)',
    route: '/student-intake',
  },
  {
    label: 'Counselors',
    body: 'Scale your impact. Manage every student.',
    cta: 'Join Free →',
    subCta: 'Free until April 7 — join before it\'s charged',
    color: colors.counselor,
    border: 'rgba(236,72,153,0.3)',
    bg: 'rgba(236,72,153,0.06)',
    borderHover: 'rgba(236,72,153,0.6)',
    bgHover: 'rgba(236,72,153,0.12)',
    route: '/counselors-waitlist',
  },
  {
    label: 'Schools',
    body: 'Full visibility across your entire school.',
    cta: 'Notify Me →',
    subCta: 'Free until April 7 — join before it\'s charged',
    color: colors.college,
    border: 'rgba(230,168,23,0.3)',
    bg: 'rgba(230,168,23,0.06)',
    borderHover: 'rgba(230,168,23,0.6)',
    bgHover: 'rgba(230,168,23,0.12)',
    route: '/colleges-waitlist',
  },
]

const RoleCardItem: React.FC<{ role: RoleCard; onSelect: () => void }> = ({ role, onSelect }) => {
  const [hovered, setHovered] = useState(false)

  return (
    <div
      onClick={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        border: `1px solid ${hovered ? role.borderHover : role.border}`,
        background: hovered ? role.bgHover : role.bg,
        borderRadius: 12,
        padding: '16px 20px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      }}
    >
      {/* Left */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{
          fontSize: 12,
          fontWeight: 600,
          fontFamily: 'Inter, sans-serif',
          color: role.color,
        }}>
          {role.label}
        </span>
        <span style={{
          fontSize: 13,
          fontFamily: 'Inter, sans-serif',
          color: 'rgba(255,255,255,0.5)',
        }}>
          {role.body}
        </span>
      </div>

      {/* Right */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, marginLeft: 16, flexShrink: 0 }}>
        <span style={{ fontSize: 13, fontFamily: 'Inter, sans-serif', color: role.color }}>
          {role.cta}
        </span>
        {role.subCta && (
          <span style={{
            fontSize: 10,
            fontFamily: 'Inter, sans-serif',
            color: 'rgba(255,255,255,0.4)',
            fontStyle: 'italic',
          }}>
            {role.subCta}
          </span>
        )}
      </div>
    </div>
  )
}

const RoleSelector: React.FC<RoleSelectorProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate()

  const handleSelect = (route: string) => {
    onClose()
    navigate(route)
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 100,
            background: 'rgba(0,0,0,0.7)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2, ease: EASE }}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#0d0d14',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 20,
              padding: '48px 40px',
              maxWidth: 480,
              width: '100%',
              position: 'relative',
            }}
          >
            {/* Close button */}
            <button
              onClick={onClose}
              style={{
                position: 'absolute',
                top: 20,
                right: 20,
                background: 'none',
                border: 'none',
                color: '#FFFFFF',
                fontSize: 20,
                fontWeight: 700,
                cursor: 'pointer',
                lineHeight: 1,
                padding: 4,
              }}
            >
              ×
            </button>

            {/* Heading */}
            <p style={{
              fontFamily: 'Cormorant Garamond, serif',
              fontSize: 28,
              fontWeight: 700,
              color: '#FFFFFF',
              margin: '0 0 8px',
            }}>
              How are you using Triptic?
            </p>

            {/* Subline */}
            <p style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 14,
              color: 'rgba(255,255,255,0.45)',
              margin: '0 0 32px',
            }}>
              Pick your path to get started.
            </p>

            {/* Role cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {ROLES.map((role) => (
                <RoleCardItem
                  key={role.label}
                  role={role}
                  onSelect={() => handleSelect(role.route)}
                />
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default RoleSelector
